import { useState, useEffect, useRef } from "react";
import { Modal, Button, InputGroup, FormControl } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import * as Icon from "react-bootstrap-icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * npm install leaflet react-leaflet
 *
 * NOTE on Nominatim (OpenStreetMap's free search/geocoding API):
 * The public nominatim.openstreetmap.org endpoint is rate-limited (~1 req/sec)
 * and meant for light use per their usage policy. Fine for dev/low-traffic
 * apps. If this doctor portal gets heavy traffic, self-host Nominatim or move
 * to a paid geocoding provider (Mapbox, LocationIQ, etc.) later — the search
 * UI below won't need to change, just the fetch URLs.
 */

// Fix default marker icon paths (common Leaflet + bundler issue where the
// marker image imports don't resolve correctly with webpack/vite).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT_CENTER = [10.85, 76.27]; // Kerala-ish fallback — change to your service area
const DEFAULT_ZOOM = 7;

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function RecenterMap({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1]]);
  return null;
}

/**
 * Props:
 *  show      - boolean, controls modal visibility
 *  onHide    - () => void
 *  onConfirm - ({ loc_address, loc_lat, loc_lng }) => void
 *  initial   - optional { loc_address, loc_lat, loc_lng } to prefill (edit mode)
 */
export default function LocationPickerModal({ show, onHide, onConfirm, initial }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [marker, setMarker] = useState(null);
  const [address, setAddress] = useState("");
  const [reverseLoading, setReverseLoading] = useState(false);
  const searchTimeout = useRef(null);

  // Reset/prefill whenever the modal opens
  useEffect(() => {
    if (!show) return;
    if (initial?.loc_lat && initial?.loc_lng) {
      setMarker({ lat: Number(initial.loc_lat), lng: Number(initial.loc_lng) });
    } else {
      setMarker(null);
    }
    setAddress(initial?.loc_address || "");
    setQuery("");
    setResults([]);
  }, [show, initial]);

  // Debounced search-as-you-type against Nominatim
  const handleQueryChange = (val) => {
    setQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.trim().length < 3) {
      setResults([]);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&q=${encodeURIComponent(val)}`
        );
        const data = await res.json();
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const reverseGeocode = async (lat, lng) => {
    setReverseLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      setAddress(data.display_name || "");
    } catch {
      // Leave address blank/whatever the user last typed — they can fix it manually.
    } finally {
      setReverseLoading(false);
    }
  };

  const pickPoint = (lat, lng) => {
    setMarker({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const selectResult = (r) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    setMarker({ lat, lng });
    setAddress(r.display_name);
    setResults([]);
    setQuery(r.display_name);
  };

  const handleConfirm = () => {
    if (!marker) return;
    onConfirm({
      loc_address: address,
      loc_lat: marker.lat.toFixed(6),
      loc_lng: marker.lng.toFixed(6),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title as="h6">
          <Icon.GeoAltFill size={14} className="me-2" />
          Pick Location
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="position-relative mb-2">
          <InputGroup>
            <InputGroup.Text><Icon.Search size={14} /></InputGroup.Text>
            <FormControl
              placeholder="Search for a place or address..."
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
            />
          </InputGroup>
          {results.length > 0 && (
            <div
              className="list-group position-absolute w-100 shadow-sm"
              style={{ zIndex: 1000, maxHeight: 220, overflowY: "auto" }}
            >
              {results.map((r) => (
                <button
                  type="button"
                  key={r.place_id}
                  className="list-group-item list-group-item-action small"
                  onClick={() => selectResult(r)}
                >
                  {r.display_name}
                </button>
              ))}
            </div>
          )}
          {searching && <div className="small text-muted mt-1">Searching…</div>}
        </div>

        <div style={{ height: 350, borderRadius: 8, overflow: "hidden" }}>
          <MapContainer
            center={marker ? [marker.lat, marker.lng] : DEFAULT_CENTER}
            zoom={marker ? 15 : DEFAULT_ZOOM}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickHandler onPick={pickPoint} />
            {marker && <RecenterMap center={[marker.lat, marker.lng]} />}
            {marker && (
              <Marker
                position={[marker.lat, marker.lng]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    pickPoint(lat, lng);
                  },
                }}
              />
            )}
          </MapContainer>
        </div>
        <div className="small text-muted mt-1">
          Click the map, drag the pin, or search above to set the location.
        </div>

        <div className="mt-3">
          <h6>Address</h6>
          <FormControl
            as="textarea"
            rows={2}
            maxLength={50}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={reverseLoading ? "Looking up address..." : "Address auto-fills, or type your own"}
          />
          <div className="small text-muted text-end">{address.length}/50</div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!marker}>
          Use This Location
        </Button>
      </Modal.Footer>
    </Modal>
  );
}